import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useOrg } from "@/context/OrgContext";
import { useFacilities } from "@/context/FacilityContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle, XCircle, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Approvals() {
  const { t } = useLang();
  const { role } = useOrg();
  const { facilities } = useFacilities();
  const [entries, setEntries] = useState([]);
  const [filterFacility, setFilterFacility] = useState("");
  const [rejectingId, setRejectingId] = useState("");
  const [reason, setReason] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [bulkRejectMode, setBulkRejectMode] = useState(false);
  const [bulkReason, setBulkReason] = useState("");

  const load = async () => {
    const params = {};
    if (filterFacility) params.facility_id = filterFacility;
    const { data } = await api.get("/approvals", { params });
    setEntries(data);
    setSelected(new Set());
    window.dispatchEvent(new CustomEvent("cl:pending-changed"));
  };

  useEffect(() => { load(); }, [filterFacility]);

  const toggleAll = (checked) => {
    setSelected(checked ? new Set(entries.map((e) => e.id)) : new Set());
  };
  const toggleOne = (id, checked) => {
    setSelected((s) => {
      const n = new Set(s);
      if (checked) n.add(id); else n.delete(id);
      return n;
    });
  };

  const approve = async (id) => {
    await api.post(`/approvals/${id}/approve`);
    toast.success(t("approvedToast"));
    load();
  };
  const reject = async (id) => {
    await api.post(`/approvals/${id}/reject`, { reason: reason || null });
    toast.success(t("rejectedToast"));
    setRejectingId(""); setReason("");
    load();
  };

  const bulk = async (action, reasonText) => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    await api.post("/approvals/bulk", { ids, action, reason: reasonText || null });
    toast.success(action === "approve" ? t("bulkApproved") : t("bulkRejected"));
    setBulkRejectMode(false); setBulkReason("");
    load();
  };

  if (role && role !== "owner") {
    return (
      <div className="max-w-2xl">
        <h1 className="font-display text-3xl font-semibold">{t("approvals")}</h1>
        <p className="text-muted-foreground mt-4">Only owners can approve entries.</p>
      </div>
    );
  }

  const allChecked = entries.length > 0 && selected.size === entries.length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data">Governance</div>
          <h1 className="font-display text-3xl font-semibold mt-1">{t("approvals")}</h1>
          <p className="text-muted-foreground text-sm mt-2 max-w-2xl">{t("approvalsDesc")}</p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="approvals-facility-filter" data-testid="approvals-facility-filter-label" className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data">
            {t("facilityFilterLabel")}
          </Label>
          <Select value={filterFacility || "all"} onValueChange={(v) => setFilterFacility(v === "all" ? "" : v)}>
            <SelectTrigger id="approvals-facility-filter" className="w-[220px]" data-testid="approvals-facility-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("allFacilities")}</SelectItem>
              {facilities.map((f) => (
                <SelectItem key={`app-ff-${f.id}`} value={f.id}>{f.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {entries.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <CheckCircle size={48} weight="duotone" className="text-primary mx-auto mb-3" />
            <div className="text-sm text-muted-foreground">{t("noPending")}</div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="font-display text-lg flex items-center gap-2">
              <Clock size={20} weight="duotone" className="text-accent" />
              {entries.length} {t("pendingApproval")}
              {selected.size > 0 && (
                <span className="text-xs font-mono-data uppercase text-primary bg-primary/10 px-2 py-1 rounded-sm ml-2" data-testid="selected-count">
                  {selected.size} {t("selected")}
                </span>
              )}
            </CardTitle>
            {selected.size > 0 && (
              <div className="flex gap-2">
                <Button size="sm" onClick={() => bulk("approve")} data-testid="bulk-approve">
                  <CheckCircle size={14} className="mr-1" /> {t("bulkApprove")} ({selected.size})
                </Button>
                <Button size="sm" variant="outline" onClick={() => setBulkRejectMode((v) => !v)} data-testid="bulk-reject-toggle">
                  <XCircle size={14} className="mr-1" /> {t("bulkReject")} ({selected.size})
                </Button>
              </div>
            )}
          </CardHeader>
          {bulkRejectMode && selected.size > 0 && (
            <div className="px-6 pb-3 flex items-center gap-2 border-b border-border/50">
              <Input placeholder={t("rejectReason")} value={bulkReason} onChange={(e) => setBulkReason(e.target.value)} data-testid="bulk-reject-reason" />
              <Button size="sm" variant="destructive" onClick={() => bulk("reject", bulkReason)} data-testid="bulk-reject-confirm">
                {t("rejectConfirm")}
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setBulkRejectMode(false); setBulkReason(""); }}>{t("cancel")}</Button>
            </div>
          )}
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-muted-foreground border-b border-border">
                  <tr>
                    <th className="pl-4 py-2 w-10"><Checkbox checked={allChecked} onCheckedChange={toggleAll} data-testid="select-all" /></th>
                    <th className="text-left px-4 py-2">{t("submittedBy")}</th>
                    <th className="text-left px-4 py-2">{t("date")}</th>
                    <th className="text-left px-4 py-2">{t("scope")}</th>
                    <th className="text-left px-4 py-2">{t("factor")}</th>
                    <th className="text-right px-4 py-2">{t("activityData")}</th>
                    <th className="text-right px-4 py-2">{t("emissions")}</th>
                    <th className="text-right px-4 py-2 w-56"></th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e) => (
                    <React.Fragment key={e.id}>
                      <tr className="border-b border-border/60" data-testid={`approval-row-${e.id}`}>
                        <td className="pl-4 py-3">
                          <Checkbox checked={selected.has(e.id)} onCheckedChange={(c) => toggleOne(e.id, c)} data-testid={`select-${e.id}`} />
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm">{e.submitted_by_name}</div>
                          <div className="text-xs text-muted-foreground font-mono-data">{e.created_at?.slice(0, 10)}</div>
                        </td>
                        <td className="px-4 py-3 font-mono-data">{e.date}</td>
                        <td className="px-4 py-3 font-mono-data">S{e.scope}</td>
                        <td className="px-4 py-3">
                          {e.factor_name}
                          {e.facility && <div className="text-xs text-muted-foreground">{e.facility}</div>}
                          {e.notes && <div className="text-xs text-muted-foreground mt-1 italic">"{e.notes}"</div>}
                        </td>
                        <td className="px-4 py-3 text-right font-mono-data">{e.activity_data} <span className="text-muted-foreground text-xs">{e.factor_unit}</span></td>
                        <td className="px-4 py-3 text-right font-mono-data font-medium">{e.emissions_kg_co2e.toFixed(2)} <span className="text-xs text-muted-foreground">kg</span></td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex gap-1 justify-end">
                            <Button size="sm" onClick={() => approve(e.id)} data-testid={`approve-${e.id}`}>
                              <CheckCircle size={14} className="mr-1" /> {t("approve")}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setRejectingId(rejectingId === e.id ? "" : e.id)} data-testid={`reject-toggle-${e.id}`}>
                              <XCircle size={14} className="mr-1" /> {t("reject")}
                            </Button>
                          </div>
                        </td>
                      </tr>
                      {rejectingId === e.id && (
                        <tr className="border-b border-border bg-destructive/5">
                          <td colSpan={8} className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <Input placeholder={t("rejectReason")} value={reason} onChange={(ev) => setReason(ev.target.value)} data-testid={`reject-reason-${e.id}`} className="flex-1" />
                              <Button size="sm" variant="destructive" onClick={() => reject(e.id)} data-testid={`reject-confirm-${e.id}`}>{t("rejectConfirm")}</Button>
                              <Button size="sm" variant="outline" onClick={() => { setRejectingId(""); setReason(""); }}>{t("cancel")}</Button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
